import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { manifest } from '@/lib/content';
import { personaBySlug, personas, type PersonaSlug } from '@/lib/personas';
import { cn } from '@/lib/utils';
import { PersonaIcon } from './PersonaIcon';

type CellState = 'full' | 'half';

/**
 * Persona -> section route mapping. Mirror of the old matrix data:
 *   full = the track starts in this section
 *   half = an optional continuation the track mentions
 */
const ROUTES: Record<PersonaSlug, Partial<Record<string, CellState>>> = {
  'principal-ai-engineer': {
    foundations: 'full',
    'error-analysis': 'full',
    'llm-as-judge': 'full',
    'rag-evals': 'full',
    'agentic-evals': 'full',
    production: 'half',
  },
  'eng-manager': {
    foundations: 'full',
    'error-analysis': 'full',
    datasets: 'full',
    production: 'full',
  },
  cto: {
    foundations: 'full',
    'safety-and-red-team': 'full',
    production: 'full',
    governance: 'full',
  },
  'product-manager': {
    foundations: 'full',
    'error-analysis': 'full',
    'llm-as-judge': 'full',
    datasets: 'full',
    production: 'full',
    statistics: 'full',
  },
  'sdet-qa': {
    'llm-as-judge': 'full',
    datasets: 'full',
    production: 'full',
    statistics: 'full',
  },
  'research-engineer': {
    datasets: 'full',
    'agentic-evals': 'full',
    'safety-and-red-team': 'full',
    governance: 'full',
    statistics: 'half',
  },
  'data-engineer': {
    datasets: 'full',
    production: 'full',
  },
  'backend-engineer': {
    foundations: 'full',
    'agentic-evals': 'full',
    production: 'full',
    datasets: 'half',
  },
  'frontend-engineer': {
    foundations: 'full',
    'error-analysis': 'full',
    'llm-as-judge': 'full',
    production: 'full',
    'agentic-evals': 'half',
  },
};

const SHORT_TITLES: Record<string, string> = {
  foundations: 'Foundations',
  'error-analysis': 'Error Analysis',
  'llm-as-judge': 'LLM-as-Judge',
  datasets: 'Datasets',
  'rag-evals': 'RAG',
  'agentic-evals': 'Agentic',
  production: 'Production',
  statistics: 'Statistics',
  'safety-and-red-team': 'Safety',
  governance: 'Governance',
};

/**
 * A quiet, always-visible descriptor under each node label. The short labels
 * keep the graph compact but hide the words an agenda-driven reader scans
 * for, so this restores them as an undertone (drawn from each section's
 * tagline) without the noise of the full title.
 */
const SUBTITLES: Record<string, string> = {
  foundations: 'first principles',
  'error-analysis': 'open coding',
  'llm-as-judge': 'model graders',
  datasets: 'golden sets',
  'rag-evals': 'faithfulness',
  'agentic-evals': 'trajectories',
  production: 'observability',
  statistics: 'significance',
  'safety-and-red-team': 'red-teaming',
  governance: 'risk & compliance',
};

interface SectionNode {
  slug: string;
  title: string;
  shortTitle: string;
  href: string;
}

function buildSections(): SectionNode[] {
  return manifest.sections
    .filter((s) => s.kind === 'learn')
    .map((s) => {
      const firstChapter = s.chapters.find((c) => !c.draft) ?? s.chapters[0];
      const href = firstChapter
        ? `/learn/${s.slug}/${firstChapter.slug}`
        : `/learn/${s.slug}`;
      return {
        slug: s.slug,
        title: s.title,
        shortTitle: SHORT_TITLES[s.slug] ?? s.title,
        href,
      };
    });
}

const W = 920;
const H = 620;
const CX = W / 2;
const CY = H / 2;
const R = 215;

export function TrackGraph({ className }: { className?: string }) {
  const sections = useMemo(buildSections, []);
  const [active, setActive] = useState<PersonaSlug | null>(null);
  const navigate = useNavigate();

  const route = active ? (ROUTES[active] ?? {}) : null;
  const activeMeta = active ? personaBySlug[active] : null;
  const fullCount = route
    ? Object.values(route).filter((v) => v === 'full').length
    : 0;

  const points = sections.map((s, i) => {
    const angle = -Math.PI / 2 + (i / sections.length) * Math.PI * 2;
    return {
      ...s,
      angle,
      x: CX + R * Math.cos(angle),
      y: CY + R * Math.sin(angle),
    };
  });

  function stateFor(slug: string): CellState | 'dim' | 'neutral' {
    if (!route) return 'neutral';
    return route[slug] ?? 'dim';
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Role selector. Choosing one lights up its route through the map. */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="mr-1 font-sans text-2xs font-medium uppercase tracking-[0.12em] text-ink-mute">
          Your role
        </span>
        {personas.map((p) => {
          const on = active === p.slug;
          return (
            <button
              key={p.slug}
              type="button"
              onClick={() => setActive(on ? null : p.slug)}
              aria-pressed={on}
              className={cn(
                'inline-flex items-center gap-1.5 border px-2.5 py-1 font-sans text-xs transition-colors',
                on
                  ? 'border-accent bg-accent text-accent-fg'
                  : 'border-rule text-ink-soft hover:border-rule-strong hover:text-ink',
              )}
            >
              <PersonaIcon persona={p.slug} size={13} />
              {p.shortName}
            </button>
          );
        })}
      </div>

      <div className="hidden md:block">
        <div className="relative border border-rule bg-bg-elev/40">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="block h-auto w-full"
            role="img"
            aria-label={
              activeMeta
                ? `Route map for ${activeMeta.name}: ${fullCount} core tracks`
                : 'Map of the ten evaluation tracks. Select a role to light up its route.'
            }
          >
            {/* One group per track: spoke + node + label. Hovering or
                focusing the group lights the whole segment copper and grows
                the node, so the affordance reads at a glance. Rendered before
                the hub so the hub caps the spoke ends. */}
            {points.map((p) => {
              const st = stateFor(p.slug);
              const right = Math.cos(p.angle) > 0.25;
              const left = Math.cos(p.angle) < -0.25;
              const side = right || left;
              const below = Math.sin(p.angle) > 0;
              const anchor = right ? 'start' : left ? 'end' : 'middle';
              const lx = p.x + (right ? 16 : left ? -16 : 0);
              // Two stacked label lines, placed clear of the node per quadrant:
              // beside it on the sides, above the top node, below the bottom one.
              const titleY = side ? p.y - 1 : below ? p.y + 22 : p.y - 28;
              const subY = side ? p.y + 13 : below ? p.y + 36 : p.y - 14;
              const accentNode = st === 'full' || st === 'half';
              const subtitle = SUBTITLES[p.slug];
              return (
                <g
                  key={p.slug}
                  role="link"
                  tabIndex={0}
                  aria-label={`${p.title} track`}
                  onClick={() => navigate(p.href)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(p.href);
                    }
                  }}
                  className={cn(
                    'group/node cursor-pointer transition-opacity duration-200 focus-visible:outline-none',
                    st === 'dim'
                      ? 'opacity-45 hover:opacity-100 focus-visible:opacity-100'
                      : 'opacity-100',
                  )}
                >
                  <title>{`${p.title} track`}</title>
                  <line
                    x1={CX}
                    y1={CY}
                    x2={p.x}
                    y2={p.y}
                    stroke="currentColor"
                    strokeWidth={st === 'full' ? 1.5 : 1}
                    strokeDasharray={st === 'half' ? '4 4' : undefined}
                    className={cn(
                      'transition-[color] duration-200',
                      st === 'full' && 'text-accent',
                      st === 'half' && 'text-accent/55',
                      st === 'dim' && 'text-rule',
                      st === 'neutral' && 'text-rule-strong',
                      'group-hover/node:text-accent group-focus-visible/node:text-accent',
                    )}
                  />
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={accentNode ? 8 : 6}
                    fill={
                      st === 'full' || st === 'neutral'
                        ? 'currentColor'
                        : 'hsl(var(--bg))'
                    }
                    stroke="currentColor"
                    strokeWidth={1.5}
                    className={cn(
                      'transition-[r,color] duration-200 ease-out',
                      accentNode ? '[r:8px]' : '[r:6px]',
                      st === 'full' && 'text-accent',
                      st === 'half' && 'text-accent',
                      st === 'dim' && 'text-rule-strong',
                      st === 'neutral' && 'text-ink-soft',
                      'group-hover/node:text-accent group-hover/node:[r:10px]',
                      'group-focus-visible/node:text-accent group-focus-visible/node:[r:10px]',
                    )}
                  />
                  <text
                    x={lx}
                    y={titleY}
                    textAnchor={anchor}
                    fill="currentColor"
                    className={cn(
                      'font-sans text-[13.5px] transition-[color] duration-200',
                      accentNode ? 'text-ink font-medium' : 'text-ink-soft',
                      'group-hover/node:text-accent group-hover/node:font-medium',
                      'group-focus-visible/node:text-accent group-focus-visible/node:font-medium',
                    )}
                  >
                    {p.shortTitle}
                  </text>
                  {subtitle ? (
                    <text
                      x={lx}
                      y={subY}
                      textAnchor={anchor}
                      fill="currentColor"
                      className={cn(
                        'font-sans text-[10.5px] tracking-tight text-ink-mute transition-[color] duration-200',
                        'group-hover/node:text-accent group-focus-visible/node:text-accent',
                      )}
                    >
                      {subtitle}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {/* hub, on top of the spoke ends */}
            <circle
              cx={CX}
              cy={CY}
              r={62}
              fill="hsl(var(--bg))"
              stroke="currentColor"
              className="text-rule-strong"
              strokeWidth={1}
            />
            {activeMeta ? (
              <>
                <text
                  x={CX}
                  y={CY - 6}
                  textAnchor="middle"
                  fill="currentColor"
                  className="text-ink font-sans text-[15px] font-semibold"
                >
                  {activeMeta.shortName}
                </text>
                <text
                  x={CX}
                  y={CY + 15}
                  textAnchor="middle"
                  fill="currentColor"
                  className="text-ink-soft font-mono text-[12px]"
                >
                  {fullCount} tracks
                </text>
              </>
            ) : (
              <>
                <text
                  x={CX}
                  y={CY - 4}
                  textAnchor="middle"
                  fill="currentColor"
                  className="text-ink font-sans text-[17px] font-semibold tracking-tight"
                >
                  AI Evals
                </text>
                <text
                  x={CX}
                  y={CY + 16}
                  textAnchor="middle"
                  fill="currentColor"
                  className="text-ink-mute font-sans text-[11px] uppercase tracking-[0.14em]"
                >
                  the field
                </text>
              </>
            )}
          </svg>
        </div>
        <p className="mt-3 font-sans text-xs text-ink-mute">
          {activeMeta
            ? 'Solid nodes start the track. Outlined nodes are optional continuations. Click any node to open it.'
            : 'Ten tracks, one field. Select a role above to light up its route, or click any node to jump in.'}
        </p>
      </div>

      {/* Mobile: the SVG labels get too dense, so the same data reads as a
          compact list of the selected (or all) sections. */}
      <div className="md:hidden">
        <ul className="flex flex-col divide-y divide-rule border border-rule">
          {points.map((p) => {
            const st = stateFor(p.slug);
            if (active && st === 'dim') return null;
            return (
              <li key={p.slug}>
                <button
                  type="button"
                  onClick={() => navigate(p.href)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-bg-mute"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full border',
                      st === 'full' && 'border-accent bg-accent',
                      st === 'half' && 'border-accent bg-bg',
                      st === 'neutral' && 'border-ink-soft bg-ink-soft',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block font-sans text-sm font-medium text-ink">
                      {p.shortTitle}
                    </span>
                    {SUBTITLES[p.slug] ? (
                      <span className="block font-sans text-xs text-ink-mute">
                        {SUBTITLES[p.slug]}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default TrackGraph;
